import React, { useEffect, useRef, useState } from "react";
import { Card, Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  listPaychecks,
  removePaycheck,
  updatePaycheck,
  duplicatePaycheck,
} from "../../redux/slices/paycheckSlice";
import RestrictionModal from "../restriction-modals/RestrictionModal";
import "./SavedPaychecksSection.scss";

// Initial page size + step. Auto-loads more 5 at a time via
// IntersectionObserver when the sentinel scrolls into view.
const PAGE_LIMIT = 5;

const formatDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatMoney = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return `$${Math.round(n).toLocaleString()}`;
};

/**
 * "Your saved paychecks" — full-list management view on the Profile page.
 * Lets the user rename, duplicate or delete each saved paycheck scenario.
 * Clicking the row name opens the Paycheck Calculator with that scenario
 * pre-loaded.
 */
const SavedPaychecksSection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const list = useSelector((s) => s.paycheck?.paycheckList || []);
  const isListLoading = useSelector((s) => s.paycheck?.isListLoading);
  const paycheckPage = useSelector((s) => s.paycheck?.paycheckPage || 0);
  const paycheckHasMore = useSelector(
    (s) => s.paycheck?.paycheckHasMore || false,
  );
  // Absolute total returned by the list endpoint. The chip count was
  // showing `list.length` (only what's been paginated in so far), which
  // jumped from 5 → 10 → 15 as the user scrolled.
  const paycheckTotal = useSelector((s) => s.paycheck?.paycheckTotal || 0);

  const [renameTarget, setRenameTarget] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [duplicatingId, setDuplicatingId] = useState(null);
  // Surfaces the upgrade modal when a duplicate hits the plan cap.
  // Mirrors how paycheck.js handles the same 403 from /save.
  const [limitInfo, setLimitInfo] = useState(null);

  // Fetch first page on mount. The chip row on the Paycheck Calculator
  // uses the same slice, so navigations between pages stay in sync.
  useEffect(() => {
    dispatch(listPaychecks({ page: 1, limit: PAGE_LIMIT, append: false }));
  }, [dispatch]);

  // Auto-load more rows when the sentinel at the bottom of the list
  // scrolls into view. Stops once paycheckHasMore is false.
  const sentinelRef = useRef(null);
  // The .sp-list container scrolls internally (capped height + overflow-y),
  // so the observer must watch it as the root — otherwise the sentinel
  // never re-enters the viewport and auto-load-more would stop firing.
  const listScrollRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !paycheckHasMore || isListLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          dispatch(
            listPaychecks({
              page: paycheckPage + 1,
              limit: PAGE_LIMIT,
              append: true,
            }),
          );
        }
      },
      {
        root: listScrollRef.current || null,
        rootMargin: "200px", // start loading a bit before the user reaches it
      },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [dispatch, paycheckPage, paycheckHasMore, isListLoading]);

  const openRename = (p) => {
    setRenameTarget(p);
    setRenameDraft(p?.custom_name || "");
  };
  const closeRename = () => {
    if (renameSaving) return;
    setRenameTarget(null);
    setRenameDraft("");
  };
  const submitRename = () => {
    if (!renameTarget) return;
    const trimmed = (renameDraft || "").trim();
    if (!trimmed) {
      toast.info("Name can't be empty");
      return;
    }
    setRenameSaving(true);
    // Update only the custom_name. Send other required fields untouched so
    // the backend Joi schema is satisfied.
    const data = {
      custom_name: trimmed,
      city: renameTarget.city || "",
      state: renameTarget.state || "",
      pay_type: renameTarget.pay_type || "salary",
      pay_by: renameTarget.pay_by || "biweekly",
      filing_status: renameTarget.filing_status || "single",
      deductions: renameTarget.deductions || {},
      gross_pay: renameTarget.gross_pay ?? null,
      net_pay: renameTarget.net_pay ?? null,
    };
    dispatch(updatePaycheck({ id: renameTarget.id, data }))
      .unwrap()
      .then(() => {
        toast.success("Renamed");
        setRenameTarget(null);
        setRenameDraft("");
        dispatch(
          listPaychecks({ page: 1, limit: PAGE_LIMIT, append: false }),
        );
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to rename");
      })
      .finally(() => setRenameSaving(false));
  };

  const handleDuplicate = (p) => {
    setDuplicatingId(p.id);
    dispatch(duplicatePaycheck(p.id))
      .unwrap()
      .then(() => {
        toast.success("Duplicated");
        dispatch(
          listPaychecks({ page: 1, limit: PAGE_LIMIT, append: false }),
        );
      })
      .catch((err) => {
        if (err?.errorType === "PAYCHECK_SCENARIO_LIMIT_REACHED") {
          setLimitInfo({
            planSlug: err?.data?.plan_slug || "free",
            planLimit: err?.data?.plan_limit ?? 1,
            totalSaved: err?.data?.total_saved ?? 0,
          });
        } else {
          toast.error(err?.message || "Failed to duplicate");
        }
      })
      .finally(() => setDuplicatingId(null));
  };

  const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    dispatch(removePaycheck(deleteTarget.id))
      .unwrap()
      .then(() => {
        toast.success("Deleted");
        setDeleteTarget(null);
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to delete");
      })
      .finally(() => setDeleting(false));
  };

  const openInCalculator = (p) => {
    // Load the scenario by routing to the paycheck tab with the id.
    // The paycheck page reads the saved list and selects the matching
    // chip; users can also tap chips directly to load.
    navigate(`/salaries-paychecks?tab=paycheck&load=${p.id}`);
  };

  return (
    <Card id="saved-paychecks" className="saved-paychecks-card mt-3" data-tour="pf-paychecks">
      <Card.Body>
        <div className="sp-head">
          <div className="sp-head-left">
            {/* Brand-tinted bookmark icon chip mirrors the rich-modal
                iconography so the section reads as part of the same family. */}
            <div className="sp-head-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h5 className="sp-head-title">Your saved paychecks</h5>
          </div>
          <span className="sp-head-count">
            {isListLoading && list.length === 0 ? (
              "Loading…"
            ) : (
              <>
                <span className="sp-head-count-dot" aria-hidden="true" />
                {paycheckTotal || list.length} saved
              </>
            )}
          </span>
        </div>

        {isListLoading && list.length === 0 && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {!isListLoading && list.length === 0 && (
          <div className="sp-empty">
            <div className="sp-empty-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <p className="sp-empty-text">
              No saved paychecks yet. Tap <strong>Save paycheck</strong> on
              the calculator to add one.
            </p>
          </div>
        )}

        {list.length > 0 && (
          <div className="sp-list" ref={listScrollRef}>
            {list.map((p, idx) => {
              const takeHome = formatMoney(p?.net_pay);
              const gross = formatMoney(p?.gross_pay);
              const dateStr = formatDate(p?.updated_at || p?.created_at);
              const isDup = duplicatingId === p.id;
              return (
                // Entire row is one large tap-target → opens the scenario
                // in the calculator. Action buttons inside call
                // e.stopPropagation so they don't trigger the row click too.
                <div
                  key={p?.id}
                  className="sp-row"
                  data-tour={idx === 0 ? "pf-pc1" : undefined}
                  role="button"
                  tabIndex={0}
                  onClick={() => openInCalculator(p)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openInCalculator(p);
                    }
                  }}
                  aria-label={`Open ${p?.custom_name || "saved paycheck"} in the calculator`}
                >
                  <div className="sp-row-main">
                    <div className="sp-row-title">
                      {p?.custom_name || "Untitled paycheck"}
                    </div>
                    <div className="sp-row-meta">
                      {takeHome && (
                        <span className="sp-amount">
                          <span className="sp-amount-label">Take-home</span>
                          <span className="sp-amount-value">{takeHome}</span>
                        </span>
                      )}
                      {!takeHome && gross && (
                        <span className="sp-amount">
                          <span className="sp-amount-label">Gross</span>
                          <span className="sp-amount-value">{gross}</span>
                        </span>
                      )}
                      {p?.pay_by && (
                        <span className="sp-freq-pill">{p.pay_by}</span>
                      )}
                      {dateStr && (
                        <>
                          <span className="sp-divider" aria-hidden="true" />
                          <span>{dateStr}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="sp-row-actions">
                    <button
                      type="button"
                      className="sp-action"
                      data-tour={idx === 0 ? "pf-rename" : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        openRename(p);
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="sp-action"
                      data-tour={idx === 0 ? "pf-dup" : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(p);
                      }}
                      disabled={isDup}
                    >
                      {isDup ? "Duplicating…" : "Duplicate"}
                    </button>
                    <button
                      type="button"
                      className="sp-action is-danger"
                      data-tour={idx === 0 ? "pf-del" : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(p);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
            {/* Sentinel — when this scrolls into view IntersectionObserver
                fires the next page fetch. Also doubles as a "loading more"
                indicator while a fetch is in flight. */}
            {paycheckHasMore && (
              <div ref={sentinelRef} className="sp-load-more">
                {isListLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <span>Loading more…</span>
                )}
              </div>
            )}
          </div>
        )}
      </Card.Body>

      {/* Rename modal — rich pattern matching the calculator's
          save-name modal (.pc-name-modal). See SavedPaychecksSection.scss. */}
      <Modal
        show={!!renameTarget}
        onHide={closeRename}
        centered
        backdrop="static"
        dialogClassName="rpm-modal"
        contentClassName="rpm-modal__content"
      >
        <Modal.Body className="rpm-modal__body">
          <button
            type="button"
            aria-label="Close"
            className="rpm-modal__close"
            onClick={closeRename}
            disabled={renameSaving}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Pencil/edit icon — signals "edit" vs the bookmark icon
              the save-name modal uses. */}
          <div className="rpm-modal__icon" aria-hidden="true">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </div>

          <h3 className="rpm-modal__title">Rename paycheck</h3>
          <p className="rpm-modal__subtitle">
            Give this scenario a name that's easy to spot in your saved
            list later.
          </p>

          {/* Show current name as a pill so the user knows what they're
              editing, even if multiple saved cards are open in the list. */}
          {renameTarget?.custom_name && (
            <div className="rpm-modal__current">
              Current: <strong>{renameTarget.custom_name}</strong>
            </div>
          )}

          <div className="rpm-modal__field">
            <label className="rpm-modal__label" htmlFor="rpm-name-input">
              Custom name
            </label>
            <input
              id="rpm-name-input"
              autoFocus
              type="text"
              className="rpm-modal__input"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              placeholder="e.g. Kaiser RN offer"
              maxLength={120}
              disabled={renameSaving}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !renameSaving) submitRename();
              }}
            />
            <div className="rpm-modal__hint">
              <span>{(renameDraft || "").length}/120 characters</span>
              <span>You can rename again later</span>
            </div>
          </div>

          <div className="rpm-modal__actions">
            <button
              type="button"
              className="rpm-modal__btn rpm-modal__btn--ghost"
              onClick={closeRename}
              disabled={renameSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rpm-modal__btn rpm-modal__btn--primary"
              onClick={submitRename}
              disabled={renameSaving || !(renameDraft || "").trim()}
            >
              {renameSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete confirmation — rich pattern, danger variant. Reuses the
          same .rpm-modal classes as the rename modal with --danger
          modifiers swapped in on the icon + primary button. */}
      <Modal
        show={!!deleteTarget}
        onHide={() => !deleting && setDeleteTarget(null)}
        centered
        backdrop="static"
        dialogClassName="rpm-modal"
        contentClassName="rpm-modal__content"
      >
        <Modal.Body className="rpm-modal__body">
          <button
            type="button"
            aria-label="Close"
            className="rpm-modal__close"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Trash icon — red palette via --danger modifier. */}
          <div
            className="rpm-modal__icon rpm-modal__icon--danger"
            aria-hidden="true"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </div>

          <h3 className="rpm-modal__title">Delete this paycheck?</h3>
          <p className="rpm-modal__subtitle">
            This scenario will be permanently removed from your saved list.
          </p>

          {/* Show exactly what's being deleted so the user can confirm
              they picked the right card. */}
          {deleteTarget?.custom_name && (
            <div className="rpm-modal__current">
              Deleting: <strong>{deleteTarget.custom_name}</strong>
            </div>
          )}

          <p className="rpm-modal__danger-note">
            This action can't be undone.
          </p>

          <div className="rpm-modal__actions">
            <button
              type="button"
              className="rpm-modal__btn rpm-modal__btn--ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rpm-modal__btn rpm-modal__btn--danger"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Plan-limit modal for the Duplicate action. Pro users get the
          "max reached, delete one" copy (no upsell); Free/Plus see the
          regular upgrade flow. Same component the save flow uses on
          /paycheck so the surfaces stay consistent. */}
      <RestrictionModal
        isVisible={Boolean(limitInfo)}
        onClose={() => setLimitInfo(null)}
        eventSource="paycheck_scenario_limit_duplicate"
        iconType="clipboard-dollar"
        heading={
          limitInfo?.planSlug === "pro"
            ? "Maximum saved scenarios reached"
            : "You've reached your saved scenario limit"
        }
        subText={
          limitInfo?.planSlug === "pro"
            ? `Pro plans cap saved scenarios at ${limitInfo?.planLimit ?? 25}. Delete an existing paycheck to duplicate this one.`
            : "Delete an existing paycheck scenario to duplicate this one, or upgrade to Pro for more scenarios."
        }
        infoText={`You're on the ${capitalize(
          limitInfo?.planSlug || "free",
        )} plan · ${limitInfo?.totalSaved ?? 0} of ${limitInfo?.planLimit ??
          1} paycheck scenarios used`}
        thirdCtaMode="newsletter"
        hideUpgradeCtas={limitInfo?.planSlug === "pro"}
      />
    </Card>
  );
};

export default SavedPaychecksSection;
